import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../../services/project.service';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { BlockScheduleModel } from '../../models/blockschedule.model';
import { SkidtemplateconfigComponent } from '../skidtemplateconfig/skidtemplateconfig';
import { ProjectStateService } from '../../services/project-state.service';
import { AssignTemplatesToSkidsRequest, ProjectModel, ProjectTemplateModel, ProjectTemplateSkidAssignmentModel } from '../../models/project.model';
import { ProjectTemplateSkidStatusModel } from '../../models/template.model';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from "primeng/table";
import { ToastModule } from "primeng/toast";
import { ProjecttemplateService } from '../../services/projecttemplate.service';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { HttpResponse } from '@angular/common/http';
import { Blockschedulebulkupdate } from '../blockschedulebulkupdate/blockschedulebulkupdate';
import { Skidinfo } from '../skidinfo/skidinfo';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { TaskManagementService } from '../../services/task-management.service';
import { ProjectTask } from '../../models/task-management.models';

type TemplateAccessState = 'editable' | 'readonly' | 'locked';


@Component({
  selector: 'app-equipment',
  imports: [CommonModule,
    CardModule,
    TagModule,
    ProgressBarModule,
    ButtonModule,
    TooltipModule,
    InputTextModule,
    FormsModule,
    MenuModule,
    DialogModule,
    MultiSelectModule,
    TableModule,
    ToastModule,
    ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './equipment.html',
  styleUrls: ['./equipment.css'],
})
export class EquipmentComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  blockScheduleList: BlockScheduleModel[] = [];
  filteredBlockScheduleList: BlockScheduleModel[] = [];
  projectId: number = 0;
  dialogRef: DynamicDialogRef | null = null;
  searchText: string = '';
  projectInfo: ProjectModel = {} as ProjectModel;
  templates: ProjectTemplateModel[] = [];
  templateStatusData: { [key: string]: ProjectTemplateSkidStatusModel } = {};
  templateAssignmentMap: { [key: string]: ProjectTemplateSkidAssignmentModel } = {};
  openTaskCounts: { [key: string]: number } = {};
  isReadOnly: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canCreateEquipment: boolean = false;
  canDeleteTemplate: boolean = false;
  currentView: 'tile' | 'list' = 'tile';
  private dataLoaded = false;
  isProjectInactive = false;
  skidNameDialogVisible: boolean = false;
  skidNameSaving: boolean = false;
  selectedSkidForName: BlockScheduleModel | null = null;
  skidNameInput: string = '';
  assignTemplatesDialogVisible: boolean = false;
  assigningTemplates: boolean = false;
  selectedAssignTemplateIds: number[] = [];
  selectedAssignSkidIds: number[] = [];
  skidTemplateAssignments: ProjectTemplateSkidAssignmentModel[] = [];
  
  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private router: Router,
    private projectStateService: ProjectStateService,
    private messageService: MessageService,
    private projecttemplateService: ProjecttemplateService,
    private permissionHelper: PermissionHelperService,
    private confirmationService: ConfirmationService,
    private taskManagementService: TaskManagementService
  ) { }
  ngOnInit(): void {  
    this.projectStateService.currentProject$.subscribe(project => {
      if (project && project.projectId) {
        this.projectInfo = project;
        console.log('Project Layout - Project from state service:', project);
      }
    });

    this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.VIEW);
      this.canCreateEquipment = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.CREATE);
      this.canEdit = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.UPDATE);
      this.canDeleteTemplate = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.tryLoadBlockSchedule();
      this.cdr.markForCheck();
    });

    this.projectStateService.pageSearch$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(search => {
      this.searchText = search;
      this.filterSkids();
    });

    // Extract projectId from URL parameters
    this.route.paramMap.subscribe(params => {
      const projectIdParam = params.get('projectId');
      if (projectIdParam) {
        this.projectId = parseInt(projectIdParam);
        console.log('Project ID from URL:', this.projectId);

        if (this.projectId > 0) {
          this.tryLoadBlockSchedule();
          this.tryLoadTemplates();
          this.loadOpenTaskCounts();
        } else {
          console.error('Invalid project ID:', this.projectId);
        }
      } else {
        console.error('No project ID found in URL parameters');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || !this.canEdit;
    this.cdr.markForCheck();
  }

  private tryLoadBlockSchedule(): void {
    if (this.dataLoaded || !this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.loadBlockSchedule();
    this.dataLoaded = true;
  }

  private tryLoadTemplates(): void {
    if (!this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.getProjectTemplate();
  }

  private getProjectTemplate(): void {
    // Backend extracts user from JWT token automatically
    this.projecttemplateService.getProjectTemplatesByUserRole(
      this.projectId,
      1 // typeId = 1 for Equipment/Skid templates
    ).subscribe({
      next: (res) => {
        console.log('Project templates filtered by user role:', res);
        this.templates = res;
        this.filterSkids();
        this.cdr.markForCheck();
        this.loadSkidTemplateAssignments();
      },
      error: (error) => {
        console.error('Error loading role-filtered templates:', error);
        this.templates = [];
        this.skidTemplateAssignments = [];
        this.templateAssignmentMap = {};
        this.templateStatusData = {};
        this.cdr.markForCheck();
      }
    });
  }

  private loadBlockSchedule(): void {
    if (!this.projectId || this.projectId <= 0) {
      console.error('Invalid project ID:', this.projectId);
      return;
    }

    this.projectService.getProjectBlockSchedule(this.projectId).subscribe({
      next: (data) => {
        console.log('Block Schedule Data:', data);
        // Ensure data is an array before assigning
        this.blockScheduleList = Array.isArray(data) ? data : [];
        this.filterSkids();
        this.cdr.markForCheck();
        this.loadSkidTemplateAssignments();
      },
      error: (error) => {
        console.error('Error loading project block schedule:', error);
        this.blockScheduleList = [];
        this.filteredBlockScheduleList = [];
      }
    });
  }

  setView(view: 'tile' | 'list'): void {
    this.currentView = view;
  }

  filterSkids(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredBlockScheduleList = [...this.blockScheduleList];
    } else {
      const searchTerms = this.getSearchTerms(this.searchText);
      this.filteredBlockScheduleList = this.blockScheduleList.filter((block) => {
        const skidFields = [
          this.getSkidDisplayTitle(block),
          block.name,
          block.blockName,
          block.tmeicSkidNo?.toString(),
          block.integratorSkidSerialNo,
          block.customerSkidNumber,
          block.inv1SerialNo,
          block.inv2SerialNo,
          block.inv3SerialNo,
          block.inv4SerialNo,
          block.inv5SerialNo,
          block.inv6SerialNo,
          block.inv7SerialNo,
          block.mvXfmrSerialNo,
          block.lvXfmrSerialNo,
        ];

        const matchesSkidField = skidFields.some((field) =>
          this.matchesSearchTerms(field, searchTerms)
        );

        if (matchesSkidField) {
          return true;
        }

        return this.getTemplatesForSkid(block.blockScheduleId!).some((template) => {
          const templateFields = [
            template.templateName,
            template.shortCode,
          ];

          return templateFields.some((field) =>
            this.matchesSearchTerms(field, searchTerms)
          );
        });
      });
    }
    this.cdr.markForCheck();
  }

  private loadOpenTaskCounts(): void {
    if (!this.projectId || this.projectId <= 0 || !this.canView) {
      this.openTaskCounts = {};
      return;
    }

    this.taskManagementService.getTasksByProject(this.projectId).subscribe({
      next: (tasks: ProjectTask[]) => {
        const nextCounts: { [key: string]: number } = {};

        (tasks || []).forEach((task) => {
          const blockScheduleId = Number(task.blockScheduleId || 0);
          const templateId = Number(task.projectTemplateId || 0);
          if (blockScheduleId <= 0 || templateId <= 0) {
            return;
          }

          const statusType = (task.statusType || '').toLowerCase();
          const statusName = (task.statusName || '').toLowerCase();
          const isCompletedLike =
            statusType === 'closed' ||
            statusType === 'verified' ||
            statusName.includes('complete') ||
            statusName.includes('closed') ||
            statusName.includes('done') ||
            statusName.includes('verified');

          if (isCompletedLike) {
            return;
          }

          const key = `${blockScheduleId}_${templateId}`;
          nextCounts[key] = (nextCounts[key] || 0) + 1;
        });

        this.openTaskCounts = nextCounts;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading equipment task counts:', error);
        this.openTaskCounts = {};
        this.cdr.markForCheck();
      }
    });
  }

  private normalizeSearchValue(value: unknown): string {
    return (value ?? '').toString().toLowerCase().trim();
  }

  private getSearchTerms(value: string): string[] {
    const normalized = this.normalizeSearchValue(value);
    if (!normalized) {
      return [];
    }

    const firstToken = normalized.split(/\s+/)[0];
    return Array.from(new Set([normalized, firstToken].filter(Boolean)));
  }

  private matchesSearchTerms(value: unknown, searchTerms: string[]): boolean {
    const normalizedValue = this.normalizeSearchValue(value);
    return searchTerms.some((term) => normalizedValue.includes(term));
  }

  clearSearch(): void {
    this.searchText = '';
    this.filterSkids();
  }

  openNewEquipment(): void {
    if (!this.canCreateEquipment || this.isProjectInactive) {
      return;
    }

    this.projectService.addCustomEquipment(this.projectId).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'New equipment created successfully' });
        this.loadBlockSchedule();
      },
      error: (error) => {
        console.error('Error creating custom equipment:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create new equipment' });
      }
    });
  }

  getSkidDisplayTitle(block: BlockScheduleModel): string {
    const customName = (block.name || block.blockName || '').trim();
    if (customName) {
      return customName;
    }
    return `${this.getEquipmentDisplayPrefix()} #${block.tmeicSkidNo ?? ''}`.trim();
  }

  private getEquipmentDisplayPrefix(): string {
    const configuredPrefix = (this.projectInfo?.prefix || '').trim();
    // Existing projects can still carry the old display prefix. Keep custom
    // project prefixes, but never expose the legacy "Skid" label to users.
    return !configuredPrefix || configuredPrefix.toLowerCase() === 'skid'
      ? 'MVPS'
      : configuredPrefix;
  }

  openEditSkidName(skidData: BlockScheduleModel, event: Event): void {
    event.stopPropagation();
    if (this.isReadOnly) return;
    this.selectedSkidForName = skidData;
    this.skidNameInput = (skidData.name || skidData.blockName || '').trim();
    this.skidNameDialogVisible = true;
  }

  closeEditSkidNameDialog(): void {
    if (this.skidNameSaving) return;
    this.skidNameDialogVisible = false;
    this.selectedSkidForName = null;
    this.skidNameInput = '';
  }

  saveSkidName(): void {
    if (!this.selectedSkidForName) return;

    const trimmedName = this.skidNameInput.trim().slice(0, 50);
    const payload: BlockScheduleModel = {
      ...this.selectedSkidForName,
      name: trimmedName || null,
      blockName: trimmedName || null
    };

    this.skidNameSaving = true;
    this.projectService.updateBlockSchedule(payload).subscribe({
      next: () => {
        this.skidNameSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'MV Power Station name updated successfully' });
        this.closeEditSkidNameDialog();
        this.loadBlockSchedule();
      },
      error: (error) => {
        console.error('Error updating skid name:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update MV Power Station name' });
      },
      complete: () => {
        this.skidNameSaving = false;
      }
    });
  }

  updateSkidInfo(skidData: any, event: Event): void {
    console.log('Edit skid invoked with data:', skidData);
    // Prevent card click event from firing
    event.stopPropagation();

    console.log('Edit skid clicked:', skidData);

    // TODO: Implement proper dialog with edit form
    const prefix = this.getEquipmentDisplayPrefix();
    this.dialogRef = this.dialogService.open(Skidinfo, {
      header: `${prefix} #${skidData.tmeicSkidNo}`,
      width: '70rem',
      height: '80vh',
      maximizable: true,
      modal: true,
      closable: true,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'auto'
      },
      data: {
        skidData: skidData,
        mode: 'edit'
      }
    });

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Skid updated:', result);
          if (result) {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Block schedule updated successfully' });
            // Reload the data to reflect changes
            this.loadBlockSchedule();

          }
        }
      }
    });
  }

  openTemplateConfig(): void {
    console.log('Opening Template Config for project:', this.projectId);

    // Open skidtemplateconfig in modal dialog
    this.dialogRef = this.dialogService.open(SkidtemplateconfigComponent, {
      header: 'MV Power Station Template Configuration',
      width: '70rem',
      height: '65vh',
      maximizable: true,
      modal: true,
      closable: true,
      contentStyle: {
        overflow: 'auto'
      },
      data: {
        projectId: this.projectId
      }
    });

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Template Config updated:', result);
          // Optionally reload the data to reflect any template changes
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template configuration updated successfully' });
          this.loadBlockSchedule();
        }
      }
    });
  }

  openSkidChecksheet(skidData: any, template?: ProjectTemplateModel): void {
    console.log('Opening skid checksheet for:', skidData, 'with template:', template);
    if (template?.templateId && this.isTemplateLocked(skidData.blockScheduleId, template.templateId)) {
      return;
    }

    // Navigate to the skid checksheet page with optional template parameter
    if (template && template.templateId) {
      this.router.navigate(['/skidchecksheet', this.projectId, skidData.blockScheduleId], {
        queryParams: { templateId: template.templateId }
      });
    } else {
      this.router.navigate(['/skidchecksheet', this.projectId, skidData.blockScheduleId]);
    }
  }

  openSkidChecksheetOld(skidData: any): void {
    console.log('Opening skid checksheet for:', skidData);

    const skidPrefix = this.getEquipmentDisplayPrefix();
    this.dialogRef = this.dialogService.open(SkidtemplateconfigComponent, {
      header: `${skidPrefix} Check Sheet: #${skidData.tmeicSkidNo}`,
      width: '100rem',
      height: '85vh',
      maximizable: true,
      modal: true,
      closable: true,
      contentStyle: {
        overflow: 'auto'
      },
      data: {
        skidData: skidData,
        projectId: this.projectId
      }
    });

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result) {
          console.log('Skid checksheet completed:', result);
          // Optionally reload the data to reflect any changes
          this.loadBlockSchedule();
        }
      }
    });
  }

  getStatus(id: number | undefined | null): string {
    if (id === 1) {
      return "Submitted";
    }
    if (id === 2) {
      return "Verified";
    }
    return "Draft";
  }

  getStatusLabel(status: number | undefined | null): string {
    if (status === null || status === undefined) {
      return 'Draft';
    }

    switch (status) {
      case 0: // ProjectTemplateStatus.Draft
        return 'Draft';
      case 1: // ProjectTemplateStatus.Submitted
        return 'Submitted';
      case 2: // ProjectTemplateStatus.Verified
        return 'Verified';
      default:
        return 'Draft';
    }
  }

  getStatusClass(status: number | undefined | null): string {
    if (status === 1) return 'submitted';
    if (status === 2) return 'verified';
    return 'draft';
  }

  getStatusSeverity(status: number | undefined | null) {
    if (status === null || status === undefined) {
      return 'warn'; // Default for Draft
    }

    switch (status) {
      case 2: // ProjectTemplateStatus.Verified
        return 'success'; // Green
      case 1: // ProjectTemplateStatus.Submitted
        return 'info'; // Blue
      case 0: // ProjectTemplateStatus.Draft
        return 'warn'; // Orange
      default:
        return 'warn'; // Default to orange (Draft)
    }
  }

  private loadSkidTemplateAssignments(): void {
    if (this.projectId <= 0 || !this.canView) return;

    this.projecttemplateService.getSkidTemplateAssignmentsByUserRole(this.projectId, 1).subscribe({
      next: (rows) => {
        this.skidTemplateAssignments = rows || [];
        this.templateAssignmentMap = {};
        this.templateStatusData = {};

        this.skidTemplateAssignments.forEach((row) => {
          const key = `${row.blockScheduleId}_${row.projectTemplateId}`;
          this.templateAssignmentMap[key] = row;
          this.templateStatusData[key] = {
            blockScheduleId: row.blockScheduleId,
            projectTemplateId: row.projectTemplateId,
            status: this.convertStatusStringToNumber((row.status || 'Draft').toString()),
            isOverride: !!row.isOverride,
            date: row.date || undefined,
            duedate: row.dueDate || undefined,
          };
        });

        this.filterSkids();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading skid template assignments:', error);
        this.skidTemplateAssignments = [];
        this.templateAssignmentMap = {};
        this.templateStatusData = {};
        this.cdr.markForCheck();
      }
    });
  }

  // Convert string status to number
  private convertStatusStringToNumber(status: string): number {
    switch (status.toLowerCase()) {
      case 'draft':
        return 0;
      case 'submitted':
        return 1;
      case 'verified':
        return 2;
      default:
        return 0;
    }
  }

  // Get template status for a specific block schedule and template
  getTemplateStatus(blockScheduleId: number, templateId: number): ProjectTemplateSkidStatusModel {
    const key = `${blockScheduleId}_${templateId}`;
    return this.templateStatusData[key] || this.getDefaultStatus(blockScheduleId, templateId);
  }

  getTemplatesForSkid(blockScheduleId: number): ProjectTemplateModel[] {
    if (!blockScheduleId || this.templates.length === 0) {
      return [];
    }

    const assignedTemplateIds = new Set(
      this.skidTemplateAssignments
        .filter((x) => x.blockScheduleId === blockScheduleId)
        .map((x) => x.projectTemplateId)
    );

    return this.templates
      .filter((t) => !!t.templateId && assignedTemplateIds.has(t.templateId!))
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }

  getOpenTaskCount(blockScheduleId: number, templateId: number): number {
    return this.openTaskCounts[`${blockScheduleId}_${templateId}`] || 0;
  }

  getTemplateAccessState(blockScheduleId: number, templateId: number): TemplateAccessState {
    const entries = this.getTemplateSequence(blockScheduleId);
    const targetIndex = entries.findIndex((entry) => entry.template.templateId === templateId);
    if (targetIndex === -1) {
      return 'locked';
    }

    if (!this.isTemplateUnlocked(entries, targetIndex)) {
      return 'locked';
    }

    if (this.isDraftStatus(entries[targetIndex].status.status)) {
      return 'editable';
    }

    return 'readonly';
  }

  isTemplateLocked(blockScheduleId: number, templateId: number): boolean {
    return this.getTemplateAccessState(blockScheduleId, templateId) === 'locked';
  }

  getTemplateLockedMessage(blockScheduleId: number, templateId: number): string {
    if (!this.isTemplateLocked(blockScheduleId, templateId)) {
      return '';
    }
    return 'Locked until the previous template is submitted or overridden.';
  }

  isTemplateDeleteDisabled(blockScheduleId: number, templateId: number): boolean {
    const key = `${blockScheduleId}_${templateId}`;
    const assignment = this.templateAssignmentMap[key];
    if (!assignment) {
      return true;
    }
    return assignment.hasChecklistResponses;
  }

  removeTemplateFromSkid(blockScheduleId: number, templateId: number, event: Event): void {
    event.stopPropagation();
    if (this.isReadOnly || !this.canDeleteTemplate) return;

    this.confirmationService.confirm({
      message: 'Are you sure you want to remove this checklist from the equipment? This action cannot be undone.',
      header: 'Remove Checklist',
      icon: 'ri-error-warning-line',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'confirm-delete-btn',
      accept: () => {
        this.projecttemplateService.unassignTemplateFromSkid(this.projectId, blockScheduleId, templateId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Template removed from MV Power Station successfully'
            });
            this.loadSkidTemplateAssignments();
          },
          error: (error) => {
            if (error?.status === 409) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Cannot Remove',
                detail: error?.error?.message || 'Checklist data exists for this template on this MV Power Station.'
              });
              return;
            }

            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error?.error?.message || 'Failed to remove template from MV Power Station'
            });
          }
        });
      }
    });
  }

  // Get default status object
  private getDefaultStatus(blockScheduleId: number, templateId: number): ProjectTemplateSkidStatusModel {
    return {
      blockScheduleId: blockScheduleId,
      projectTemplateId: templateId,
      status: 0, // ProjectTemplateStatus.Draft
      isOverride: false
    };
  }

  openAssignTemplatesDialog(): void {
    this.selectedAssignTemplateIds = [];
    this.selectedAssignSkidIds = [];
    this.assignTemplatesDialogVisible = true;
  }

  closeAssignTemplatesDialog(): void {
    if (this.assigningTemplates) return;
    this.assignTemplatesDialogVisible = false;
  }

  saveAssignTemplatesToSkids(): void {
    if (this.selectedAssignTemplateIds.length === 0 || this.selectedAssignSkidIds.length === 0) {
      return;
    }

    const request: AssignTemplatesToSkidsRequest = {
      projectId: this.projectId,
      templateIds: Array.from(new Set(this.selectedAssignTemplateIds)),
      blockScheduleIds: Array.from(new Set(this.selectedAssignSkidIds))
    };

    this.assigningTemplates = true;
    this.projecttemplateService.assignTemplatesToSkids(request).subscribe({
      next: () => {
        this.assigningTemplates = false;
        this.assignTemplatesDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Templates assigned to selected MV Power Stations successfully'
        });
        this.loadSkidTemplateAssignments();
      },
      error: (error) => {
        this.assigningTemplates = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.message || 'Failed to assign templates to MV Power Stations'
        });
      }
    });
  }

  getTemplateAssignOptions(): { label: string; value: number }[] {
    return this.templates
      .filter((t): t is ProjectTemplateModel & { templateId: number } => typeof t.templateId === 'number')
      .map((t) => ({
        label: `${t.shortCode || t.templateName} (${t.templateName})`,
        value: t.templateId
      }));
  }

  getSkidAssignOptions(): { label: string; value: number }[] {
    return this.blockScheduleList
      .filter((s): s is BlockScheduleModel & { blockScheduleId: number } => typeof s.blockScheduleId === 'number')
      .map((s) => ({
        label: this.getSkidDisplayTitle(s),
        value: s.blockScheduleId
      }));
  }

  exportBlockSchedule() {
    this.projectService.downloadBlockSchedule(this.projectId).subscribe(async (event) => {
      if (event != null) {
        let fileName = `${this.projectInfo.projectNumber}_BlockSchedule.xlsx`;
        console.log(fileName);
        let data = event as HttpResponse<Blob>;
        console.log(event);
        const downloadedFile = new Blob([data.body as BlobPart], {
          type: data.body?.type
        });
        if (downloadedFile.type != "") {
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = fileName;
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
        }
      }

    });
  }




  private getTemplateSequence(blockScheduleId: number): { template: ProjectTemplateModel; status: ProjectTemplateSkidStatusModel }[] {
    return this.getTemplatesForSkid(blockScheduleId).map((template) => ({
      template,
      status: this.getTemplateStatus(blockScheduleId, template.templateId!)
    }));
  }

  private isTemplateUnlocked(entries: { template: ProjectTemplateModel; status: ProjectTemplateSkidStatusModel }[], targetIndex: number): boolean {
    let unlocked = true;

    for (let index = 0; index <= targetIndex; index += 1) {
      if (index === 0) {
        unlocked = true;
      } else {
        const previousEntry = entries[index - 1];
        unlocked = unlocked && (
          !this.isDraftStatus(previousEntry.status.status) ||
          !!previousEntry.status.isOverride
        );
      }

      if (index === targetIndex) {
        return unlocked;
      }
    }

    return false;
  }

  private isDraftStatus(status: number | undefined | null): boolean {
    return (status ?? 0) === 0;
  }
}
